import { Grid, Paper, Typography } from "@mui/material";
import { DBPosition } from "../api/types";
import { useMemo } from "react";
import {
  compactDollar,
  dollar,
  getPositionPnLDollar,
  multiplyOrZero,
} from "../utils/utils";

// Same close reasons the profit graph counts as a realised trade
const TRADED_CLOSE_REASONS = ["TAKE_PROFIT", "STOP_LOSS", "HEDGE_REPLACED"];

type Stat = { label: string; value: string; sub?: string; color?: string };

const buildStats = (positions: DBPosition[]): Stat[] => {
  const fees = positions.reduce(
    (tot, pos) =>
      tot + (pos.openTransaction.fee ?? 0) + (pos.closeTransaction?.fee ?? 0),
    0,
  );

  // Traded notional of every filled open and close
  const volume = positions.reduce((tot, pos) => {
    const { openTransaction: open, closeTransaction: close } = pos;
    const openUsd = open?.filledTimestamp
      ? multiplyOrZero(open.coinAmount, open.price)
      : 0;
    const closeUsd = close?.filledTimestamp
      ? multiplyOrZero(close.coinAmount, close.price)
      : 0;
    return tot + Math.abs(openUsd) + Math.abs(closeUsd);
  }, 0);

  const pnls = positions
    .filter(
      (pos) =>
        pos.status === "CLOSED" &&
        TRADED_CLOSE_REASONS.includes(pos.closeReason ?? ""),
    )
    .map((pos) => getPositionPnLDollar(pos))
    .filter((pnl): pnl is number => pnl !== undefined && !isNaN(pnl));

  const wins = pnls.filter((pnl) => pnl > 0).length;
  const totalPnl = pnls.reduce((tot, pnl) => tot + pnl, 0);

  // Equity when the first trade in the range was opened
  const startEquity = positions
    .filter(
      (pos) =>
        pos.openTransaction?.filledTimestamp && pos.openTransaction.totalEquity,
    )
    .sort(
      (a, b) =>
        new Date(a.openTransaction.filledTimestamp!).getTime() -
        new Date(b.openTransaction.filledTimestamp!).getTime(),
    )[0]?.openTransaction.totalEquity;

  const signed = (value: string, n: number) => (n > 0 ? `+${value}` : value);

  return [
    {
      label: "Winrate",
      value: pnls.length ? `${((wins / pnls.length) * 100).toFixed(1)}%` : "-",
      sub: `${wins}W / ${pnls.length - wins}L`,
    },
    {
      label: "Closed trades",
      value: pnls.length.toString(),
      sub: pnls.length
        ? `${signed(dollar(totalPnl / pnls.length), totalPnl)} avg per trade`
        : undefined,
    },
    {
      label: "Realised PnL",
      value: signed(dollar(totalPnl), totalPnl),
      sub: startEquity
        ? `${signed(((totalPnl / startEquity) * 100).toFixed(1), totalPnl)}% of starting equity`
        : undefined,
      color:
        totalPnl > 0 ? "success.main" : totalPnl < 0 ? "error.main" : undefined,
    },
    {
      label: "Fees",
      value: dollar(fees),
      sub: volume
        ? `${((fees / volume) * 100).toFixed(3)}% of volume`
        : undefined,
    },
    {
      label: "Volume",
      value: compactDollar(volume),
      sub: pnls.length
        ? `${compactDollar(volume / pnls.length)} per trade`
        : undefined,
    },
  ];
};

export default function AccountStatistics({
  positions,
}: {
  positions: DBPosition[];
}) {
  const stats = useMemo(() => buildStats(positions), [positions]);

  return (
    <Grid container spacing={1}>
      {stats.map((stat) => (
        <Grid key={stat.label} size={{ xs: 6, md: "grow" }}>
          <Paper variant="outlined" sx={{ px: 1.5, py: 1, height: "100%" }}>
            <Typography variant="caption" color="text.secondary">
              {stat.label}
            </Typography>
            <Typography
              variant="h6"
              sx={{ lineHeight: 1.3 }}
              color={stat.color}
            >
              {stat.value}
            </Typography>
            {stat.sub && (
              <Typography variant="caption" color="text.secondary">
                {stat.sub}
              </Typography>
            )}
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}
